// ============================================================
// FILE: src/pages/Contact.jsx  →  CONTACT PAGE
// The inquiry form, contact info cards and Google Map.
// Phone / email / WhatsApp / address are in
// src/data/site.js (CONTACT DETAILS).
// To move the map pin: change the address in the Google Maps
// link below (search for "google.com/maps").
// ============================================================

import { Mail, MapPin, Phone, Clock, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import InquiryForm from "../components/InquiryForm";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { CONTACT_INFO } from "../data/site";

export default function Contact() {
  useSEO("Contact Us — Evolve Trade & Logistics", "Start a trade inquiry. Our trade desk responds within 24 hours with quotations, samples and lane advice.");
  return (
    <>
      <PageHero
        label="Contact"
        title="Let's move"
        accent="your cargo"
        description="Tell us what you need to buy, sell or ship. A dedicated trade specialist responds within 24 hours."
        crumbs={[{ label: "Contact" }]}
      />

      <section className="pb-24 sm:pb-32" data-testid="contact-section">
        <div className="container-x grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <div className="card-surface" data-testid="contact-form-card">
              <h2 className="font-display text-2xl font-semibold tracking-tight">Start a Trade Inquiry</h2>
              <p className="mt-2 text-sm text-muted-foreground">Fields marked * are required. Your details stay confidential.</p>
              <div className="mt-8">
                <InquiryForm type="contact" testIdPrefix="contact" />
              </div>
            </div>
          </Reveal>

          <Stagger className="space-y-5">
            {[
              { icon: Mail, label: "Email", value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}`, testId: "contact-email" },
              { icon: Phone, label: "Phone", value: CONTACT_INFO.phone, href: `tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`, testId: "contact-phone" },
              { icon: Phone, label: "Alternate Phone", value: CONTACT_INFO.phone2, href: `tel:${CONTACT_INFO.phone2.replace(/\s/g, "")}`, testId: "contact-phone-2" },
              { icon: MessageCircle, label: "WhatsApp", value: "Chat with our trade desk", href: `https://wa.me/${CONTACT_INFO.whatsapp}`, testId: "contact-whatsapp" },
              { icon: MapPin, label: "Head Office", value: CONTACT_INFO.address, testId: "contact-address" },
              { icon: Clock, label: "Working Hours", value: CONTACT_INFO.hours, testId: "contact-hours" },
            ].map((c) => (
              <StaggerItem key={c.label}>
                <div className="glass flex items-start gap-4 rounded-2xl p-5 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-teal-500/50" data-testid={c.testId}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-700/15 to-teal-500/15">
                    <c.icon className="h-5 w-5 text-teal-500" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="mt-1 block text-sm font-semibold transition-colors hover:text-teal-500">
                        {c.value}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm font-semibold leading-relaxed">{c.value}</p>
                    )}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>

        <Reveal className="mt-14">
          <div className="overflow-hidden rounded-[2rem] border border-slate-900/10 dark:border-white/10" data-testid="contact-map">
            {/* ===== GOOGLE MAP ===== */}
            {/* To move the map pin, change the address inside src="...maps?q=YOUR+ADDRESS..." below. */}
            <iframe
              title="Evolve Trade & Logistics — Head Office, Mumbai"
              src="https://www.google.com/maps?q=Bandra%20Kurla%20Complex%2C%20Mumbai&output=embed"
              className="h-[380px] w-full grayscale-[35%] contrast-[1.05]"
              loading="lazy"
            />
          </div>
        </Reveal>
      </section>
    </>
  );
}
